import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";

type AuthStatus = "loading" | "authed" | "anon";

interface AuthState {
  status: AuthStatus;
  username: string | null;
  mustChangePassword: boolean;
}

interface AuthApi extends AuthState {
  login: (username: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (current: string, next: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    username: null,
    mustChangePassword: false,
  });

  const applySession = useCallback(
    (s: { authenticated: boolean; username: string | null; mustChangePassword: boolean }) => {
      setState({
        status: s.authenticated ? "authed" : "anon",
        username: s.username,
        mustChangePassword: s.mustChangePassword,
      });
    },
    [],
  );

  const refresh = useCallback(async () => {
    try {
      applySession(await api.session());
    } catch {
      setState({ status: "anon", username: null, mustChangePassword: false });
    }
  }, [applySession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string, remember: boolean) => {
      applySession(await api.login(username, password, remember));
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setState({ status: "anon", username: null, mustChangePassword: false });
    }
  }, []);

  const changePassword = useCallback(
    async (current: string, next: string) => {
      applySession(await api.changePassword(current, next));
    },
    [applySession],
  );

  const value = useMemo<AuthApi>(
    () => ({ ...state, login, logout, changePassword, refresh }),
    [state, login, logout, changePassword, refresh],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
